if (!customElements.get('image-with-text-story')) {
  class ImageWithTextStory extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.image-with-text__story-track')
      this.slides = Array.from(this.querySelectorAll('.image-with-text__story-slide'))
      this.indicators = Array.from(this.querySelectorAll('.image-with-text__story-indicator'))
      this.previousButton = this.querySelector('.image-with-text__story-previous')
      this.nextButton = this.querySelector('.image-with-text__story-next')

      if (!this.track || this.slides.length < 2 || this.indicators.length !== this.slides.length) return

      this.duration = Number(this.dataset.duration) || 5000
      this.activeIndex = 0
      this.pauseReasons = new Set()
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
      this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)')

      this.handleMouseEnter = () => this.setPaused('hover', true)
      this.handleMouseLeave = () => this.setPaused('hover', false)
      this.handleFocusIn = () => this.setPaused('focus', true)
      this.handleFocusOut = (event) => {
        if (!event.relatedTarget || !this.contains(event.relatedTarget)) this.setPaused('focus', false)
      }
      this.handleVisibilityChange = () => this.setPaused('document', document.hidden)
      this.handleReducedMotionChange = () => this.setPaused('reduced-motion', this.reducedMotionQuery.matches)
      this.handleHoverChange = () => this.syncHoverListeners()
      this.handleIntersection = (entries) => {
        const entry = entries.find(({ target }) => target === this)
        if (entry) this.setPaused('viewport', !entry.isIntersecting)
      }
      this.handleTransitionEnd = (event) => {
        if (event.target !== this.track || this.pendingResetIndex === null) return
        const resetIndex = this.pendingResetIndex
        this.pendingResetIndex = null
        this.jumpToTrackIndex(resetIndex)
      }
      this.handlePointerDown = (event) => this.onPointerDown(event)
      this.handlePointerMove = (event) => this.onPointerMove(event)
      this.handlePointerUp = (event) => this.onPointerEnd(event)
      this.handlePointerCancel = (event) => this.onPointerEnd(event, true)
      this.handlePreviousClick = () => this.goRelative(-1)
      this.handleNextClick = () => this.goRelative(1)

      this.leadingClone = this.createBoundaryClone(this.slides[this.slides.length - 1])
      this.trailingClone = this.createBoundaryClone(this.slides[0])
      this.track.prepend(this.leadingClone)
      this.track.appendChild(this.trailingClone)
      this.jumpToTrackIndex(1)
      this.pendingResetIndex = null

      this.indicators.forEach((indicator) => {
        const onClick = () => {
          if (Date.now() < this.ignoreClickUntil) return
          this.goTo(Number(indicator.dataset.storyIndex))
        }
        indicator.storyClickHandler = onClick
        indicator.addEventListener('click', onClick)
      })

      this.previousButton?.addEventListener('click', this.handlePreviousClick)
      this.nextButton?.addEventListener('click', this.handleNextClick)
      this.addEventListener('focusin', this.handleFocusIn)
      this.addEventListener('focusout', this.handleFocusOut)
      this.addEventListener('pointerdown', this.handlePointerDown)
      this.addEventListener('pointermove', this.handlePointerMove)
      this.addEventListener('pointerup', this.handlePointerUp)
      this.addEventListener('pointercancel', this.handlePointerCancel)
      this.track.addEventListener('transitionend', this.handleTransitionEnd)
      document.addEventListener('visibilitychange', this.handleVisibilityChange)
      this.addMediaListener(this.reducedMotionQuery, this.handleReducedMotionChange)
      this.addMediaListener(this.hoverQuery, this.handleHoverChange)
      this.syncHoverListeners()

      if ('IntersectionObserver' in window) {
        this.pauseReasons.add('viewport')
        this.intersectionObserver = new IntersectionObserver(this.handleIntersection, { threshold: 0.01 })
        this.intersectionObserver.observe(this)
      }

      if (document.hidden) this.pauseReasons.add('document')
      if (this.reducedMotionQuery.matches) this.pauseReasons.add('reduced-motion')

      this.updateState()
      this.startProgress()
    }

    disconnectedCallback() {
      this.progressAnimation?.cancel()
      this.progressAnimation = null
      this.removeEventListener('mouseenter', this.handleMouseEnter)
      this.removeEventListener('mouseleave', this.handleMouseLeave)
      this.removeEventListener('focusin', this.handleFocusIn)
      this.removeEventListener('focusout', this.handleFocusOut)
      this.removeEventListener('pointerdown', this.handlePointerDown)
      this.removeEventListener('pointermove', this.handlePointerMove)
      this.removeEventListener('pointerup', this.handlePointerUp)
      this.removeEventListener('pointercancel', this.handlePointerCancel)
      this.track?.removeEventListener('transitionend', this.handleTransitionEnd)
      this.previousButton?.removeEventListener('click', this.handlePreviousClick)
      this.nextButton?.removeEventListener('click', this.handleNextClick)
      document.removeEventListener('visibilitychange', this.handleVisibilityChange)
      this.removeMediaListener(this.reducedMotionQuery, this.handleReducedMotionChange)
      this.removeMediaListener(this.hoverQuery, this.handleHoverChange)
      this.intersectionObserver?.disconnect()
      this.indicators?.forEach((indicator) => {
        indicator.removeEventListener('click', indicator.storyClickHandler)
        delete indicator.storyClickHandler
      })
      this.leadingClone?.remove()
      this.trailingClone?.remove()
    }

    createBoundaryClone(slide) {
      const clone = slide.cloneNode(true)
      clone.dataset.storyClone = 'true'
      clone.setAttribute('aria-hidden', 'true')
      clone.removeAttribute('role')
      clone.removeAttribute('aria-roledescription')
      clone.removeAttribute('aria-label')
      return clone
    }

    addMediaListener(query, handler) {
      if (query.addEventListener) query.addEventListener('change', handler)
      else query.addListener(handler)
    }

    removeMediaListener(query, handler) {
      if (!query) return
      if (query.removeEventListener) query.removeEventListener('change', handler)
      else query.removeListener(handler)
    }

    syncHoverListeners() {
      this.removeEventListener('mouseenter', this.handleMouseEnter)
      this.removeEventListener('mouseleave', this.handleMouseLeave)
      this.setPaused('hover', false)

      if (this.hoverQuery.matches) {
        this.addEventListener('mouseenter', this.handleMouseEnter)
        this.addEventListener('mouseleave', this.handleMouseLeave)
      }
    }

    setPaused(reason, shouldPause) {
      if (shouldPause) this.pauseReasons.add(reason)
      else this.pauseReasons.delete(reason)
      this.syncPlayback()
    }

    syncPlayback() {
      if (!this.progressAnimation) return
      if (this.pauseReasons.size > 0) this.progressAnimation.pause()
      else this.progressAnimation.play()
    }

    startProgress() {
      this.progressAnimation?.cancel()
      const progress = this.indicators[this.activeIndex]?.querySelector('.image-with-text__story-progress')
      if (!progress) return

      this.progressAnimation = progress.animate(
        [
          { transform: 'translateY(-50%) scaleX(0)' },
          { transform: 'translateY(-50%) scaleX(1)' },
        ],
        { duration: this.duration, easing: 'linear', fill: 'forwards' },
      )
      this.progressAnimation.onfinish = () => this.goRelative(1, true)
      this.syncPlayback()
    }

    goRelative(direction, isAutomatic = false) {
      const targetIndex = (this.activeIndex + direction + this.slides.length) % this.slides.length
      this.goTo(targetIndex, direction, isAutomatic)
    }

    goTo(index, direction = 0, _isAutomatic = false) {
      if (!Number.isInteger(index) || index < 0 || index >= this.slides.length) return

      const previousIndex = this.activeIndex
      let trackIndex = index + 1
      this.pendingResetIndex = null

      if (direction > 0 && previousIndex === this.slides.length - 1 && index === 0) {
        trackIndex = this.slides.length + 1
        this.pendingResetIndex = 1
      } else if (direction < 0 && previousIndex === 0 && index === this.slides.length - 1) {
        trackIndex = 0
        this.pendingResetIndex = this.slides.length
      }

      this.activeIndex = index
      this.setTrackPosition(trackIndex)
      this.updateState()
      this.startProgress()

      if (this.pendingResetIndex !== null && this.reducedMotionQuery.matches) {
        requestAnimationFrame(() => {
          if (this.pendingResetIndex === null) return
          const resetIndex = this.pendingResetIndex
          this.pendingResetIndex = null
          this.jumpToTrackIndex(resetIndex)
        })
      }
    }

    setTrackPosition(trackIndex, pixelOffset = 0) {
      const offset = pixelOffset === 0 ? '' : ` + ${pixelOffset}px`
      this.track.style.transform = `translate3d(calc(-${trackIndex * 100}%${offset}), 0, 0)`
    }

    jumpToTrackIndex(trackIndex) {
      this.track.style.transition = 'none'
      this.setTrackPosition(trackIndex)
      this.track.getBoundingClientRect()
      this.track.style.removeProperty('transition')
    }

    onPointerDown(event) {
      if (!event.isPrimary || event.pointerType === 'mouse') return

      this.dragPointerId = event.pointerId
      this.dragStartX = event.clientX
      this.dragStartY = event.clientY
      this.dragAxis = null
      this.dragOffset = 0
      this.setPaused('gesture', true)
    }

    onPointerMove(event) {
      if (event.pointerId !== this.dragPointerId) return

      const deltaX = event.clientX - this.dragStartX
      const deltaY = event.clientY - this.dragStartY

      if (this.dragAxis === null && (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8)) {
        this.dragAxis = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical'
        if (this.dragAxis === 'horizontal') {
          this.track.classList.add('is-dragging')
          this.setPointerCapture?.(event.pointerId)
        } else {
          this.setPaused('gesture', false)
        }
      }

      if (this.dragAxis !== 'horizontal') return
      event.preventDefault()
      this.dragOffset = deltaX
      this.setTrackPosition(this.activeIndex + 1, deltaX)
    }

    onPointerEnd(event, wasCancelled = false) {
      if (event.pointerId !== this.dragPointerId) return

      const wasHorizontal = this.dragAxis === 'horizontal'
      const threshold = this.clientWidth * 0.15
      const shouldNavigate = !wasCancelled && wasHorizontal && Math.abs(this.dragOffset) >= threshold

      this.dragPointerId = null
      this.track.classList.remove('is-dragging')
      this.pauseReasons.delete('gesture')

      if (shouldNavigate) {
        this.ignoreClickUntil = Date.now() + 300
        this.goRelative(this.dragOffset < 0 ? 1 : -1)
      } else {
        this.track.getBoundingClientRect()
        this.setTrackPosition(this.activeIndex + 1)
        this.syncPlayback()
      }

      this.dragAxis = null
      this.dragOffset = 0
    }

    updateState() {
      this.slides.forEach((slide, index) => slide.setAttribute('aria-hidden', index === this.activeIndex ? 'false' : 'true'))

      this.indicators.forEach((indicator, index) => {
        const progress = indicator.querySelector('.image-with-text__story-progress')
        if (index === this.activeIndex) indicator.setAttribute('aria-current', 'true')
        else indicator.removeAttribute('aria-current')
        if (progress) progress.style.transform = `translateY(-50%) scaleX(${index < this.activeIndex ? 1 : 0})`
      })
    }
  }

  customElements.define('image-with-text-story', ImageWithTextStory)
}
